import { useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiPostLive } from '../../lib/api-client';
import { stashPendingOfferFiles } from '../../lib/pending-offer-files';
import { apiError } from '../../lib/utils';
import { uploadProposalFile, uploadQuotationFile } from './ProposalFiles';
import { Button } from '../ui/Button';
import { Dialog } from '../ui/Dialog';
import { Field, TextArea } from '../ui/Input';
import { useNotice } from '../ui/Notice';

const MAX_FILES = 5;
const MAX_BYTES = 25 * 1024 * 1024;

function fileBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const value = String(reader.result || '');
      const at = value.indexOf(',');
      resolve(at >= 0 ? value.slice(at + 1) : value);
    };
    reader.onerror = () => reject(reader.error || new Error('Could not read that file'));
    reader.readAsDataURL(file);
  });
}

export function AddOfferPartDialog({
  kind, customerId, title, proposalId, quotationId, onClose,
}: {
  kind: 'proposal' | 'quotation';
  customerId: string;
  title: string;
  proposalId?: string;
  quotationId?: string;
  onClose: () => void;
}) {
  const navigate = useNavigate();
  const notice = useNotice();
  const [instruction, setInstruction] = useState('');
  const [files, setFiles] = useState<File[]>([]);
  const [busy, setBusy] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const label = kind === 'quotation' ? 'quotation' : 'proposal';

  function addFiles(list: FileList | null) {
    const next = [...files];
    for (const file of Array.from(list || [])) {
      if (next.length >= MAX_FILES) {
        notice.failure(`Attach at most ${MAX_FILES} files`);
        break;
      }
      if (file.size > MAX_BYTES) {
        notice.failure(`${file.name} is over 25 MB`);
        continue;
      }
      if (!next.some((item) => item.name === file.name && item.size === file.size)) next.push(file);
    }
    setFiles(next);
  }

  async function upload(id: string) {
    const send = kind === 'quotation' ? uploadQuotationFile : uploadProposalFile;
    for (const file of files) await send(id, file);
  }

  async function attachmentPayload() {
    return Promise.all(files.map(async (file) => ({
      name: file.name,
      mimeType: file.type || 'application/octet-stream',
      dataBase64: await fileBase64(file),
    })));
  }

  async function draft() {
    setBusy(true);
    try {
      const attachments = await attachmentPayload();
      const row = await notice.run(`Drafting the ${label}`, (log, signal) => apiPostLive<{ proposalId?: string; quotationId?: string }>('/api/sales-offers/companion', {
        proposalId: kind === 'quotation' ? proposalId : undefined,
        quotationId: kind === 'proposal' ? quotationId : undefined,
        instruction,
        attachments,
      }, log, signal), `${label[0].toUpperCase()}${label.slice(1)} created`);
      if (!row) return;
      const id = kind === 'quotation' ? row.quotationId : row.proposalId;
      if (id && files.length) {
        try {
          await upload(id);
        } catch (err) {
          notice.failure(apiError(err, 'The document was created. A file was not attached.'));
        }
      }
      onClose();
      if (kind === 'quotation' && row.quotationId) navigate(`/quotations/${row.quotationId}`);
      else if (row.proposalId) navigate(`/proposals/${row.proposalId}`);
    } finally {
      setBusy(false);
    }
  }

  function manual() {
    const params = new URLSearchParams();
    if (customerId) params.set('customerId', customerId);
    if (title) params.set('title', title);
    if (kind === 'quotation' && proposalId) params.set('proposalId', proposalId);
    if (kind === 'proposal' && quotationId) params.set('quotationId', quotationId);
    if (files.length) {
      stashPendingOfferFiles(kind, files);
      params.set('attach', '1');
    }
    onClose();
    navigate(kind === 'quotation' ? `/quotations/new?${params}` : `/proposals/new?${params}`);
  }

  return (
    <Dialog title={`Add ${label}`} onClose={() => { if (!busy) onClose(); }} footer={
      <>
        <Button variant="outline" disabled={busy} onClick={onClose}>Cancel</Button>
        <Button variant="outline" disabled={busy} onClick={manual}>Create manually</Button>
        <Button disabled={busy} onClick={() => void draft()}>Draft with assistant</Button>
      </>
    }>
      <p className="text-sm">This offer has no {label}. Tell the assistant what to include, or start a blank {label} and fill it yourself.</p>
      <Field label="Instruction">
        <TextArea value={instruction} onChange={(event) => setInstruction(event.target.value)} placeholder="Optional. Leave blank to draft from the document that already exists. A quotation still needs a written unit price." />
      </Field>
      <div className="space-y-2">
        <p className="text-sm">{kind === 'proposal' ? 'Attachments are read and rewritten into the proposal sections, then stored with it.' : 'Attachments are read for the quotation, then stored with it. A price is used only when it is written in the file.'}</p>
        {files.map((file) => (
          <div key={`${file.name}-${file.size}`} className="flex items-center justify-between gap-2 text-sm">
            <span className="min-w-0 truncate">{file.name}</span>
            <button type="button" className="shrink-0 text-red-600" disabled={busy} onClick={() => setFiles(files.filter((item) => item !== file))}>Remove</button>
          </div>
        ))}
        <Button variant="outline" disabled={busy} onClick={() => inputRef.current?.click()}>Attach a file</Button>
        <input ref={inputRef} className="hidden" type="file" multiple disabled={busy} onChange={(event) => { addFiles(event.target.files); event.target.value = ''; }} />
      </div>
    </Dialog>
  );
}
